import React, { memo, useState, useEffect, useMemo } from "react";
import {
  Drawer,
  Row,
  Col,
  Space,
  Typography,
  Image,
  Card,
  Table,
  theme,
  Button,
  Select,
  Empty,
  Tag,
} from "antd";
import { CloseOutlined, LeftOutlined } from "@ant-design/icons";
import scalesConfig from "../../../../helpers/physioscaleskeys";
import bedImg from "../../../../assets/images/patientIcons/bed.svg";
import ScaleTableOp from "./progressTableOp";
import ProgressChartOp from "./progressChartOp";
import tableIcon from "../../../../assets/images/table.svg";
import tableIconActive from "../../../../assets/images/activetable.svg";
import chartIcon from "../../../../assets/images/bar-chart-2.svg";
import chartIconActive from "../../../../assets/images/active-bar-chart-2.svg";
import { scaleLabels } from "./scaleOptions";
import { UiButton, UiTag } from "../../../ui";
import "./progressScalesOp.css";
const isEmrRecord = (record) => record?.data?.type === "EMR";
const ROM_HIDDEN_KEYS = ["key", "id", "fullData", "type"];

const scaleKeyMap = {
  CoordinationTest: "CoordinationTest",
  MMT: "MMT",
  MAS: "MAS",
  ROM: "ROM",
  VCG: "VCG",
  ASIA: "ASIA",
  RPE: "RPE",
  VAS: "VAS",
  RASS: "RASS",
  RAS: "RASS",
  MOCA: "MOCA",
  NIHSS: "NIHSS",
  GCS: "GCS",
  FGT: "FingerGripTest",
  PFMT: "PeakFlowMeterTest",
  TCMS: "TrunkControlTest",
  BI: "BarthelIndex",
  "6MWT": "SixMinuteWalkTest",
  six_min_walk: "SixMinuteWalkTest",
  REFLEXES: "Reflexes",
  SixMinuteWalkTest: "SixMinuteWalkTest",
};

const buildJointWiseTables = (records = []) => {
  const joints = {};

  records.forEach((item) => {
    if (!item?.data) return;

    const date = renderISTDate(item.created_at);

    Object.entries(item.data).forEach(([jointKey, values]) => {
      if (!values || typeof values !== "object") return;

      const hasAnyValue = Object.values(values).some((v) => {
        if (v === null || v === undefined) return false;
        if (typeof v === "string") return v.trim() !== "";
        if (typeof v === "number") return Number.isFinite(v);
        if (typeof v === "object") return Object.keys(v || {}).length > 0;
        return Boolean(v);
      });
      if (!hasAnyValue) return;

      if (!joints[jointKey]) joints[jointKey] = [];

      joints[jointKey].push({
        key: `${jointKey}-${item.id || date}`,
        date,
        id: item.id,
        fullData: item.data,
        type: item.type,
        ...values,
      });
    });
  });

  return joints;
};

const getRomChartData = (data, scaleKey) => {
  const romSubItems = scalesConfig[scaleKey]?.subItems || {};
  const jointData = {};

  Object.keys(romSubItems).forEach((jointKey) => {
    jointData[jointKey] = data
      .map((item) => ({
        name: new Date(item.created_at).toLocaleString(),
        _ts: new Date(item.created_at).getTime(),
        ...(item.data?.[jointKey] || {}),
      }))
      .filter((row) =>
        Object.keys(row).some((k) => k !== "name" && k !== "_ts")
      )
      .sort((a, b) => a._ts - b._ts);
  });

  return jointData;
};

const asiaGradeMap = {
  A: 1,
  B: 2,
  C: 3,
  D: 4,
  E: 5,
};

const convertAsiaGrade = (value) => {
  if (!value) return null;
  return asiaGradeMap[value.toUpperCase()] || null;
};
const convertNeurologicalLevel = (level) => {
  if (!level) return null;

  const match = level.match(/([A-Z])(\d+)/);
  if (!match) return null;

  const region = match[1];
  const num = parseInt(match[2], 10);

  const offsets = {
    C: 0,
    T: 8,
    L: 20,
    S: 25,
  };

  return offsets[region] !== undefined ? offsets[region] + num : null;
};

const getScaleData = (scaleKey, records = []) => {
  return records
    .map((item) => {
      const row = {
        name: new Date(item.created_at).toLocaleString(),
        _ts: new Date(item.created_at).getTime(),
        ...item.data,
      };

      if (scaleKey === "ASIA") {
        row.nerological_level_of_injury = convertNeurologicalLevel(
          item.data?.nerological_level_of_injury
        );

        row.asia_impairment_scale = convertAsiaGrade(
          item.data?.asia_impairment_scale
        );
      }

      return row;
    })
    .sort((a, b) => a._ts - b._ts);
};

const renderISTDate = (value) => {
  if (!value) return "—";

  return new Date(value).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const ExpandableText = ({ text, collapsedLines = 2, maxWidth = 220 }) => {
  const [expanded, setExpanded] = useState(false);
  const value = (text ?? "").toString();
  const trimmed = value.trim();
  const display = trimmed.length ? trimmed : "—";
  const shouldToggle = trimmed.length > 60 || trimmed.includes("\n");

  return (
    <div
      style={{
        maxWidth,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      }}
    >
      <Typography.Paragraph
        style={{
          marginBottom: 0,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "break-all",
        }}
        ellipsis={
          !shouldToggle ? false : expanded ? false : { rows: collapsedLines }
        }
      >
        {display}
      </Typography.Paragraph>
      {shouldToggle && trimmed.length > 0 && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: "auto" }}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Less" : "More"}
        </Button>
      )}
    </div>
  );
};

const getTableColumns = (setEditData, setFormVisible) => ({
  ASIA: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    {
      title: "Neurological Level of Injury",
      dataIndex: "nerological_level_of_injury",
      key: "nerological_level_of_injury",
    },
    {
      title: "ASIA Impairment Scale",
      dataIndex: "asia_impairment_scale",
      key: "asia_impairment_scale",
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                data: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],
  GCS: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    { title: "Score", dataIndex: "score", key: "score" },
    {
      title: "Eye Opening Response",
      dataIndex: "eye_opening_response",
      key: "eye_opening_response",
    },
    {
      title: "Verbal Response",
      dataIndex: "verbal_response",
      key: "verbal_response",
    },
    {
      title: "Motor Response",
      dataIndex: "motor_response",
      key: "motor_response",
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 220,
      render: (text) => <ExpandableText text={text} collapsedLines={2} />,
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                gcs: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],
  RASS: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    { title: "RAS Score", dataIndex: "rass_scale", key: "rass_scale" },
    {
      title: "Interpretation",
      dataIndex: "interpretation",
      key: "interpretation",
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 220,
      render: (text) => <ExpandableText text={text} collapsedLines={2} />,
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData(record);
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],
  TrunkControlTest: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    { title: "Total Score", dataIndex: "score", key: "score" },
    {
      title: "Interpretation",
      dataIndex: "interpretation",
      key: "interpretation",
    },
    {
      title: "Sitting Balance",
      dataIndex: "sitting_balance",
      key: "sitting_balance",
    },
    {
      title: "Dynamic Reaching",
      dataIndex: "sitting_to_standing",
      key: "sitting_to_standing",
    },
    {
      title: "Selective Movement Control",
      dataIndex: "selective_movement_control",
      key: "selective_movement_control",
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 220,
      render: (text) => <ExpandableText text={text} collapsedLines={2} />,
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                data: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],

  MOCA: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    { title: "Score", dataIndex: "score", key: "score" },
    {
      title: "Interpretation",
      dataIndex: "interpretation",
      key: "interpretation",
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 220,
      render: (text) => <ExpandableText text={text} collapsedLines={2} />,
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                data: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],
  NIHSS: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    { title: "Score", dataIndex: "score", key: "score" },
    {
      title: "Interpretation",
      dataIndex: "interpretation",
      key: "interpretation",
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                data: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],
  VAS: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    { title: "Pain", dataIndex: "pain", key: "pain" },
    // { title: "Score", dataIndex: "score", key: "score" },
    {
      title: "Interpretation",
      dataIndex: "interpretation",
      key: "interpretation",
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 220,
      render: (text) => <ExpandableText text={text} collapsedLines={2} />,
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                data: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],
  RPE: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    { title: "Exertion", dataIndex: "exertion", key: "exertion" },
    // { title: "Score", dataIndex: "score", key: "score" },
    {
      title: "Interpretation",
      dataIndex: "interpretation",
      key: "interpretation",
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 220,
      render: (text) => <ExpandableText text={text} collapsedLines={2} />,
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                data: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],
  BarthelIndex: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    { title: "Score", dataIndex: "score", key: "score" },
    {
      title: "Interpretation",
      dataIndex: "interpretation",
      key: "interpretation",
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                data: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],
  FingerGripTest: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    { title: "Age", dataIndex: "age", key: "age" },

    {
      title: "Left Hand Strength (kg)",
      dataIndex: "left_hand",
      key: "left_hand",
      render: (value, record) => (value ? `${value}` : "N/A"),
    },

    {
      title: "Right Hand Strength (kg)",
      dataIndex: "right_hand",
      key: "right_hand",
      render: (value, record) => (value ? `${value}` : "N/A"),
    },

    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 220,
      render: (text) => <ExpandableText text={text} collapsedLines={2} />,
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                data: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],

  PeakFlowMeterTest: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },
    { title: "Gender", dataIndex: "gender", key: "gender" },
    { title: "Age", dataIndex: "age", key: "age" },
    { title: "Height (cm)", dataIndex: "height", key: "height" },
    {
      title: "Predicted PEFR",
      dataIndex: "predicted_PEFR",
      key: "predicted_PEFR",
    },
    { title: "Test PEFR", dataIndex: "test_PEFR", key: "test_PEFR" },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 220,
      render: (text) => <ExpandableText text={text} collapsedLines={2} />,
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                data: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],
  SixMinuteWalkTest: [
    { title: "Date", dataIndex: "date", key: "date", render: renderISTDate },

    { title: "HR (Pre)", dataIndex: "heart_rate", key: "heart_rate" },
    {
      title: "HR (Post)",
      dataIndex: "post_vital_heart_rate",
      key: "post_vital_heart_rate",
    },

    { title: "SBP (Pre)", dataIndex: "sbp", key: "sbp" },
    { title: "SBP (Post)", dataIndex: "post_vital_sbp", key: "post_vital_sbp" },

    { title: "DBP (Pre)", dataIndex: "dbp", key: "dbp" },
    { title: "DBP (Post)", dataIndex: "post_vital_dbp", key: "post_vital_dbp" },

    { title: "SpO₂ (Pre)", dataIndex: "spo2", key: "spo2" },
    {
      title: "SpO₂ (Post)",
      dataIndex: "post_vital_spo2",
      key: "post_vital_spo2",
    },

    { title: "RR (Pre)", dataIndex: "rr", key: "rr" },
    { title: "RR (Post)", dataIndex: "post_vital_rr", key: "post_vital_rr" },

    { title: "RPE (Pre)", dataIndex: "rpe", key: "rpe" },
    { title: "RPE (Post)", dataIndex: "post_vital_rpe", key: "post_vital_rpe" },

    { title: "No. of Laps", dataIndex: "no_of_laps", key: "no_of_laps" },
    {
      title: "Distance (m)",
      dataIndex: "distance_covered",
      key: "distance_covered",
    },
    {
      title: "Action",
      render: (_, record) =>
        record?.type === "EMR" ? null : (
          <Button
            type="link"
            onClick={() => {
              setEditData({
                id: record.id,
                data: record,
              });
              setFormVisible(true);
            }}
          >
            Edit
          </Button>
        ),
    },
  ],
});

const ScaleModalOp = memo(
  ({
    patientData,
    scaleKey,
    data,
    isVisible,
    onClose,
    onAddScale,
    returnToTable,
    onViewModeConsumed,
  }) => {
    const normalizedScaleKey = scaleKeyMap[scaleKey] || scaleKey;
    const normalizedData = (data || []).map((item) => ({
      created_at: item.created_at || item.date,
      data: item.data ?? item,
      type: item.type,
      id: item.id,
    }));
    const { token } = theme.useToken();
    const [viewMode, setViewMode] = useState("table");
    const scaleType = scalesConfig[scaleKeyMap[scaleKey]]?.type;
    const jointTables = buildJointWiseTables(normalizedData);
    const [editData, setEditData] = useState(null);
    const [formVisible, setFormVisible] = useState(false);
    const [isMobileViewport, setIsMobileViewport] = useState(false);
    const [romMobileJointKey, setRomMobileJointKey] = useState(null);

    useEffect(() => {
      const mq = window.matchMedia("(max-width: 768px)");
      const onChange = () => setIsMobileViewport(mq.matches);
      onChange();
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }, []);

    const romChartDataMap = useMemo(
      () => getRomChartData(normalizedData, scaleKey),
      [normalizedData, scaleKey]
    );
    const romJointKeys = Object.keys(romChartDataMap);
    const hasAnyRomTableRows = useMemo(
      () =>
        romJointKeys.some(
          (jointKey) => (jointTables?.[jointKey] || []).length > 0
        ),
      [romJointKeys, jointTables]
    );
    const romSelectedJoint =
      romMobileJointKey && romChartDataMap[romMobileJointKey]
        ? romMobileJointKey
        : romJointKeys[0];

    useEffect(() => {
      if (romMobileJointKey && !romChartDataMap[romMobileJointKey]) {
        setRomMobileJointKey(null);
      }
    }, [romChartDataMap, romMobileJointKey]);
    const columnsMap = getTableColumns((editPayload) => {
      if (isEmrRecord(editPayload)) return;
      setEditData(editPayload);
    }, setFormVisible);

    useEffect(() => {
      if (editData) {
        setViewMode("table");
      }
    }, [editData]);

    useEffect(() => {
      if (formVisible && editData) {
        onAddScale(scaleKeyMap[scaleKey] || scaleKey, editData);
        setFormVisible(false);
      }
    }, [formVisible, editData, scaleKey, onAddScale]);
    useEffect(() => {
      if (returnToTable) {
        setViewMode("table");
        onViewModeConsumed?.();
      }
    }, [returnToTable]);

    const resolvedScaleKey = scaleKeyMap[scaleKey] || scaleKey;
    const showToggle = [
      "GCS",
      "ROM",
      "TrunkControlTest",
      "RASS",
      "MOCA",
      "NIHSS",
      "VAS",
      "RPE",
      "BarthelIndex",
      "FingerGripTest",
      "ASIA",
      "PeakFlowMeterTest",
      "SixMinuteWalkTest",
    ].includes(resolvedScaleKey);
    const addScaleAllowed = [
      "GCS",
      "ROM",
      "TrunkControlTest",
      "RASS",
      "MOCA",
      "NIHSS",
      "VAS",
      "RPE",
      "BarthelIndex",
      "FingerGripTest",
      "ASIA",
      "PeakFlowMeterTest",
      "SixMinuteWalkTest",
      "MMT",
      "MAS",
      "VCG",
      "CoordinationTest",
    ];
    const buildRomColumns = (jointKey) => {
      const jointConfig = scalesConfig.ROM?.subItems?.[jointKey]?.keys || {};

      return [
        {
          title: "Date",
          dataIndex: "date",
          key: "date",
          render: (value) => <span style={{ whiteSpace: "nowrap" }}>{value}</span>,
        },
        ...Object.keys(jointConfig)
          .filter((key) => !ROM_HIDDEN_KEYS.includes(key))
          .map((key) => ({
            title:
              jointConfig[key]?.name || key.replace(/_/g, " ").toUpperCase(),
            dataIndex: key,
            key,
            render: (value) =>
              typeof value === "number" ? `${value}` : value || "—",
          })),
        {
          title: "Action",
          render: (_, record) =>
            record?.type === "EMR" ? null : (
              <Button
                type="link"
                onClick={() => {
                  setEditData({
                    id: record.id,
                    data: record.fullData,
                    jointKey,
                  });
                  setFormVisible(true);
                }}
              >
                Edit
              </Button>
            ),
        },
      ];
    };

    return (
      <Drawer
        open={isVisible}
        closable={false}
        width="100vw"
        placement="right"
        className="scale-modal-drawer"
        bodyStyle={{
          backgroundColor: "#F3F5F8",
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: 0,
        }}
        title={
          <Row className="scale-mobile-header">
            <Col flex="auto" className="scale-header-primary">
              <div className="scale-header-primary-inner">
                <Space>
                  <LeftOutlined
                    onClick={onClose}
                    style={{ fontSize: 18, cursor: "pointer" }}
                  />
                  <Typography className="patient-name">
                    {`${patientData?.first_name || ""} ${
                      patientData?.last_name || ""
                    }`}
                  </Typography>
                  {patientData?.bed_no && (
                    <Tag color="orange">
                      <Image src={bedImg} preview={false} />{" "}
                      {patientData?.bed_no}
                    </Tag>
                  )}
                </Space>
                {showToggle && (
                  <div className="scale-header-mobile-toggles">
                    <div
                      className="mobile-toggle-btn"
                      onClick={() => setViewMode("table")}
                      style={{
                        background:
                          viewMode === "table" ? token.colorPrimary : "#fff",
                        borderColor:
                          viewMode === "table" ? token.colorPrimary : "#d0d5dd",
                      }}
                    >
                      <img
                        src={viewMode === "table" ? tableIconActive : tableIcon}
                        alt=""
                      />
                    </div>
                    <div
                      className="mobile-toggle-btn"
                      onClick={() => setViewMode("graph")}
                      style={{
                        background:
                          viewMode === "graph" ? token.colorPrimary : "#fff",
                        borderColor:
                          viewMode === "graph" ? token.colorPrimary : "#d0d5dd",
                      }}
                    >
                      <img
                        src={viewMode === "graph" ? chartIconActive : chartIcon}
                        alt=""
                      />
                    </div>
                  </div>
                )}
              </div>
            </Col>
            <Col className="header-sub">
              <Typography className="scale-modal-subtitle">
                {scalesConfig[resolvedScaleKey]?.expandedName ||
                  resolvedScaleKey}
              </Typography>
            </Col>
            <Col className="header-actions scale-header-actions-desktop">
              <Space align="center">
                <Space align="center">
                  {showToggle && (
                    <div className="desktop-toggle">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          background: token.colorPrimaryBg,
                          borderRadius: "10px",
                          padding: "4px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          width: "90px",
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          onClick={() => setViewMode("table")}
                          style={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "center",
                            background:
                              viewMode === "table"
                                ? token.colorPrimary
                                : "transparent",
                            borderRadius: "10px",
                            padding: "8px 0",
                            cursor: "pointer",
                          }}
                        >
                          <img
                            src={
                              viewMode === "table" ? tableIconActive : tableIcon
                            }
                            width={20}
                          />
                        </div>

                        <div
                          onClick={() => setViewMode("graph")}
                          style={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "center",
                            background:
                              viewMode === "graph"
                                ? token.colorPrimary
                                : "transparent",
                            borderRadius: "10px",
                            padding: "8px 0",
                            cursor: "pointer",
                          }}
                        >
                          <img
                            src={
                              viewMode === "graph" ? chartIconActive : chartIcon
                            }
                            width={20}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {showToggle && (
                    <div className="mobile-toggle-icons">
                      <div
                        className="mobile-toggle-btn"
                        onClick={() => setViewMode("table")}
                        style={{
                          background:
                            viewMode === "table" ? token.colorPrimary : "#fff",
                          borderColor:
                            viewMode === "table"
                              ? token.colorPrimary
                              : "#d0d5dd",
                        }}
                      >
                        <img
                          src={
                            viewMode === "table" ? tableIconActive : tableIcon
                          }
                        />
                      </div>

                      <div
                        className="mobile-toggle-btn"
                        onClick={() => setViewMode("graph")}
                        style={{
                          background:
                            viewMode === "graph" ? token.colorPrimary : "#fff",
                          borderColor:
                            viewMode === "graph"
                              ? token.colorPrimary
                              : "#d0d5dd",
                        }}
                      >
                        <img
                          src={
                            viewMode === "graph" ? chartIconActive : chartIcon
                          }
                        />
                      </div>
                    </div>
                  )}

                  {addScaleAllowed.includes(resolvedScaleKey) && (
                    <UiButton
                      size="md"
                      className="scale-modal-add-btn"
                      onClick={() =>
                        onAddScale(scaleKeyMap[scaleKey] || scaleKey, null)
                      }
                    >
                      Add Scale
                    </UiButton>
                  )}
                </Space>

                <CloseOutlined
                  onClick={onClose}
                  className="scale-modal-close-icon"
                />
              </Space>
            </Col>
          </Row>
        }
      >
        <div className="op-scale-drawer-content">
          <Row gutter={[24, 24]} style={{ padding: "0 10px" }}>
            {scaleType === "table" ? (
              <Col
                span={24}
                style={{ display: "flex", justifyContent: "center" }}
              >
                <ScaleTableOp
                  scaleKey={scaleKey}
                  data={data}
                  onEdit={(record) => {
                    if ((scaleKeyMap[scaleKey] || scaleKey) === "MMT") {
                      setEditData({
                        id: record.id,
                        data: {
                          upper_left_limb: record.upper_left_limb_score,
                          lower_left_limb: record.lower_left_limb_score,
                          upper_right_limb: record.upper_right_limb_score,
                          lower_right_limb: record.lower_right_limb_score,
                          comments: record.comments,
                        },
                      });
                    } else {
                      setEditData(record);
                    }

                    setFormVisible(true);
                  }}
                />
              </Col>
            ) : normalizedScaleKey === "ROM" ? (
              viewMode === "graph" ? (
                isMobileViewport && romJointKeys.length > 0 ? (
                  <Col span={24}>
                    <div className="op-rom-mobile-graph-wrap">
                      <Select
                        className="op-rom-mobile-select"
                        placeholder="Select joint"
                        value={romSelectedJoint}
                        onChange={(v) => setRomMobileJointKey(v)}
                        options={romJointKeys.map((jk) => ({
                          value: jk,
                          label:
                            scalesConfig[scaleKey]?.subItems?.[jk]?.name ||
                            jk.replace(/_/g, " "),
                        }))}
                      />
                      <Card
                        className="op-rom-graph-card op-graph-card"
                        style={{ marginBottom: 16 }}
                      >
                        <ProgressChartOp
                          key={romSelectedJoint}
                          scaleKey={scaleKeyMap[scaleKey]}
                          tags={Object.keys(
                            scalesConfig[scaleKey]?.subItems?.[romSelectedJoint]
                              ?.keys || {}
                          )}
                          chartData={romChartDataMap[romSelectedJoint] || []}
                          subItemKey={romSelectedJoint}
                          height={330}
                          useMobileMetricDropdown
                        />
                      </Card>
                    </div>
                  </Col>
                ) : (
                  Object.entries(romChartDataMap).map(
                    ([jointKey, chartData]) => (
                      <Col
                        xs={24}
                        sm={24}
                        md={24}
                        lg={12}
                        xl={12}
                        key={jointKey}
                      >
                        <Card
                          className="op-graph-card"
                          style={{ marginBottom: 24 }}
                        >
                          <h4>
                            {scalesConfig[scaleKey]?.subItems[jointKey]?.name}
                          </h4>
                          <ProgressChartOp
                            scaleKey={scaleKeyMap[scaleKey]}
                            tags={Object.keys(
                              scalesConfig[scaleKey]?.subItems?.[jointKey]
                                ?.keys || {}
                            )}
                            chartData={chartData}
                            subItemKey={jointKey}
                            height={330}
                          />
                        </Card>
                      </Col>
                    )
                  )
                )
              ) : (
                <Col span={24}>
                  {isMobileViewport ? (
                    <div className="op-rom-mobile-graph-wrap">
                      <Select
                        className="op-rom-mobile-select"
                        placeholder="Select joint"
                        value={romSelectedJoint}
                        onChange={(v) => setRomMobileJointKey(v)}
                        options={romJointKeys.map((jk) => ({
                          value: jk,
                          label:
                            scalesConfig[scaleKey]?.subItems?.[jk]?.name ||
                            jk.replace(/_/g, " "),
                        }))}
                      />

                      {(() => {
                        const rows = jointTables?.[romSelectedJoint] || [];
                        if (!rows.length) {
                          return (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="No data"
                            />
                          );
                        }
                        return (
                          <Table
                            key={romSelectedJoint}
                            columns={buildRomColumns(romSelectedJoint)}
                            dataSource={rows}
                            pagination={false}
                            bordered
                            scroll={{ x: "max-content" }}
                            locale={{ emptyText: "No data" }}
                          />
                        );
                      })()}
                    </div>
                  ) : !hasAnyRomTableRows ? (
                    <Card style={{ marginBottom: 16 }}>
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No data"
                      />
                    </Card>
                  ) : (
                    romJointKeys
                      .filter(
                        (jointName) =>
                          (jointTables?.[jointName] || []).length > 0
                      )
                      .map((jointName) => {
                        const rows = jointTables?.[jointName] || [];
                        return (
                          <Card key={jointName} style={{ marginBottom: 16 }}>
                            <h3 style={{ marginBottom: 12 }}>
                              {(
                                scalesConfig[scaleKey]?.subItems?.[jointName]
                                  ?.name || jointName.replace(/_/g, " ")
                              ).toUpperCase()}
                            </h3>

                            <Table
                              columns={buildRomColumns(jointName)}
                              dataSource={rows}
                              pagination={false}
                              bordered
                              scroll={{ x: "max-content" }}
                              locale={{ emptyText: "No data" }}
                            />
                          </Card>
                        );
                      })
                  )}
                </Col>
              )
            ) : showToggle ? (
              viewMode === "graph" ? (
                <Card className="op-graph-card" style={{ width: "100%" }}>
                   {" "}
                  <ProgressChartOp
                    scaleKey={resolvedScaleKey}
                    tags={Object.keys(
                      scalesConfig[resolvedScaleKey]?.keys || {}
                    )}
                    chartData={getScaleData(resolvedScaleKey, normalizedData)}
                    height={330}
                  />
                </Card>
              ) : (
                <Col span={24}>
                  <Table
                    columns={columnsMap[resolvedScaleKey] || []}
                    dataSource={data.map((item, i) => {
                      const scaleType = scaleKeyMap[scaleKey];
                      let rowData = item?.data || {};
                      if (scaleType === "PeakFlowMeterTest") {
                        if (
                          normalizedData?.predict_pefr !== undefined &&
                          normalizedData?.predicted_PEFR === undefined
                        ) {
                          normalizedData.predicted_PEFR =
                            normalizedData.predict_pefr;
                        }
                      }
                      return {
                        key: i,
                        id: item.id,
                        date: item.created_at,
                        ...rowData,
                      };
                    })}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: "max-content" }}
                    bordered
                  />
                </Col>
              )
            ) : (
              <Card className="op-graph-card" style={{ width: "100%" }}>
                <ProgressChartOp
                  scaleKey={scaleKeyMap[scaleKey]}
                  tags={Object.keys(
                    scalesConfig[scaleKeyMap[scaleKey]]?.keys || {}
                  )}
                  chartData={getScaleData(scaleKey, data)}
                  height={330}
                />
              </Card>
            )}
          </Row>
        </div>

        <div
          className="op-scale-mobile-footer"
          style={{ ["--op-scale-primary"]: token.colorPrimary }}
        >
          {addScaleAllowed.includes(resolvedScaleKey) && (
            <Button
              type="primary"
              onClick={() =>
                onAddScale(scaleKeyMap[scaleKey] || scaleKey, null)
              }
              block
            >
              Add Scale
            </Button>
          )}
          <Button onClick={onClose} block>
            Cancel
          </Button>
        </div>
      </Drawer>
    );
  }
);

export default ScaleModalOp;
